import React, { useState, useEffect, cache } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Car,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Hash,
  Calendar,
  Gauge,
  AlertCircle,
  Check,
  Loader,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { MOCK_US_STATES } from "../PostAuthenticationSurvey";
import VehicleInfoService from "../../lib/VehicleInfoService";

const YEARS = Array.from(
  { length: 25 },
  (_, i) => new Date().getFullYear() - i
);

const ExtendedWarrantyVehicleCompletion = ({
  selectedVehicle,
  onComplete,
  onBack,
}) => {
  const { user, getIdToken } = useAuth();
  // Determine what information we need to collect
  const hasVIN = selectedVehicle?.vin && selectedVehicle.vin.length > 5;
  const hasLicenseAndState = selectedVehicle?.plate && selectedVehicle?.state;

  const [formData, setFormData] = useState(() => {
    // First check sessionStorage for cached vehicle data
    const cachedData = sessionStorage.getItem("extendedWarrantyVehicleData");

    // Get state from contact info cache as a fallback
    let stateFromContact = "";
    try {
      const cachedAddressComponents = sessionStorage.getItem(
        "contactInfoAddressComponents"
      );
      if (cachedAddressComponents) {
        const addressData = JSON.parse(cachedAddressComponents);
        stateFromContact = addressData.state || "";
      }
    } catch (e) {
      console.error("Error parsing cached contact data:", e);
    }

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        let d = {
          vin: parsed.vin || selectedVehicle?.vin || "",
          license: parsed.license || selectedVehicle?.plate || "",
          state:
            parsed.state || stateFromContact  || selectedVehicle?.state || "",
          year: parsed.year || selectedVehicle?.year || "",
          make: parsed.make || selectedVehicle?.make || "",
          model: parsed.model || selectedVehicle?.model || "",
          mileage: parsed.mileage || selectedVehicle?.mileage || "",
          externalId: selectedVehicle?.externalId || null,
          source: selectedVehicle?.source || "MANUAL",
        };
        console.log(d);
        return d;
      } catch (e) {
        console.error("Error parsing cached vehicle data:", e);
      }
    }
    // Fallback to selectedVehicle data or contact cache
    return {
      vin: selectedVehicle?.vin || "",
      license: selectedVehicle?.plate || "",
      state: stateFromContact || selectedVehicle?.state || "",
      year: selectedVehicle?.year || "",
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
      mileage: selectedVehicle?.mileage || "",
      externalId: selectedVehicle?.externalId || null,
      source: selectedVehicle?.source || "MANUAL",
    };
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState({
    makes: [],
    models: [],
    years: [],
  });

  const [loadingStates, setLoadingStates] = useState({
    makes: false,
    models: false,
  });

  useEffect(() => {
    if (Object.values(formData).some((val) => val !== "" && val !== null)) {
      sessionStorage.setItem(
        "extendedWarrantyVehicleData",
        JSON.stringify(formData)
      );
    }
  }, [formData]);

  // Fetch makes on component mount
  useEffect(() => {
    const loadMakes = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, makes: true }));
        const token = await getIdToken();
        const makes = await VehicleInfoService.fetchVehicleMakes(token);
        setVehicleOptions((prev) => ({ ...prev, makes }));
      } catch (error) {
        console.error("Error fetching makes:", error);
        setErrors((prev) => ({
          ...prev,
          general: "Failed to load vehicle makes",
        }));
      } finally {
        setLoadingStates((prev) => ({ ...prev, makes: false }));
      }
    };

    if (user && !hasVIN) {
      // Only fetch if we need to show the dropdown
      loadMakes();
    }
  }, [user, getIdToken, hasVIN]);

  // Fetch models when make changes
  useEffect(() => {
    const loadModels = async () => {
      if (!formData.make) {
        setVehicleOptions((prev) => ({ ...prev, models: [] }));
        return;
      }

      try {
        setLoadingStates((prev) => ({ ...prev, models: true }));
        const token = await getIdToken();
        const models = await VehicleInfoService.fetchVehicleModels(
          formData.make,
          token
        );
        setVehicleOptions((prev) => ({ ...prev, models }));
      } catch (error) {
        console.error("Error fetching models:", error);
        setErrors((prev) => ({
          ...prev,
          general: "Failed to load vehicle models",
        }));
      } finally {
        setLoadingStates((prev) => ({ ...prev, models: false }));
      }
    };

    loadModels();
  }, [formData.make, getIdToken]);

  // Validation rules based on what information we have
  const getRequiredFields = () => {
    if (hasVIN) {
      // If we have VIN, we need state and mileage
      return {
        vin: "VIN is required",
        state: "State is required",
        mileage: "Mileage is required",
      };
    } else if (hasLicenseAndState) {
      // If we have license + state, we need make, model, year, mileage
      return {
        state: "State is required",
        make: "Make is required",
        model: "Model is required",
        year: "Year is required",
        mileage: "Mileage is required",
      };
    } else {
      // Fallback - need everything
      return {
        state: "State is required",
        make: "Make is required",
        model: "Model is required",
        year: "Year is required",
        mileage: "Mileage is required",
      };
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();
    console.log(requiredFields);
    Object.entries(requiredFields).forEach(([field, errorMessage]) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = errorMessage;
      }
    });

    // Additional validation
    if (
      formData.mileage &&
      (isNaN(formData.mileage) || parseInt(formData.mileage) < 0)
    ) {
      newErrors.mileage = "Please enter a valid mileage";
    }

    if (formData.vin && formData.vin.length > 0 && formData.vin.length !== 17) {
      newErrors.vin = "VIN must be exactly 17 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Store the completed vehicle data
      const completeVehicleData = {
        ...formData,
        completedAt: new Date().toISOString(),
        originalVehicle: selectedVehicle,
      };

      sessionStorage.setItem(
        "completeVehicleData",
        JSON.stringify(completeVehicleData)
      );

      // Call onComplete with the finalized data
      if (onComplete) {
        onComplete(completeVehicleData);
      }
    } catch (err) {
      console.error("Error completing vehicle information:", err);
      setErrors({
        general: "Failed to process vehicle information. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get the completion scenario text
  const getScenarioDescription = () => {
    if (hasVIN) {
      return "We have your VIN! We just need to confirm your state and current mileage.";
    } else if (hasLicenseAndState) {
      return "We have your license plate and state. Please provide your vehicle details and current mileage.";
    } else {
      return "Please provide your complete vehicle information for the extended warranty quote.";
    }
  };

  const getHeaderTitle = () => {
    if (hasVIN) {
      return "Confirm Vehicle Details";
    } else {
      return "Complete Vehicle Information";
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <Car className="w-8 h-8 text-blue-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{getHeaderTitle()}</h1>
        <p className="text-gray-600">{getScenarioDescription()}</p>
      </div>

      {/* Selected Vehicle Summary */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-blue-500 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Selected Vehicle</p>
              <p className="text-sm text-blue-700">
                {selectedVehicle?.year} {selectedVehicle?.make}{" "}
                {selectedVehicle?.model}
                {selectedVehicle?.license && ` • ${selectedVehicle.license}`}
                {selectedVehicle?.state && ` • ${selectedVehicle.state}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Vehicle Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* VIN Field - show if we have VIN or need to collect it */}
          {hasVIN && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                VIN{" "}
                {getRequiredFields().vin ? (
                  <span className="text-red-500">*</span>
                ) : (
                  ""
                )}
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) =>
                    handleInputChange("vin", e.target.value.toUpperCase())
                  }
                  className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vin ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter 17-character VIN"
                  maxLength={17}
                  readOnly={hasVIN}
                  disabled={hasVIN}
                />
              </div>
              {errors.vin && (
                <p className="text-red-500 text-sm">{errors.vin}</p>
              )}
            </div>
          )}

          {/* License Plate - show if we don't have VIN */}
          {/* {!hasVIN && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                License Plate <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.license}
                onChange={(e) =>
                  handleInputChange("license", e.target.value.toUpperCase())
                }
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.license ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter license plate"
              />
              {errors.license && (
                <p className="text-red-500 text-sm">{errors.license}</p>
              )}
            </div>
          )} */}

          {/* State */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              State <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <select
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                  errors.state ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select state...</option>
                {MOCK_US_STATES.map((state) => (
                  <option key={state.text} value={state.value}>
                    {state.text}
                  </option>
                ))}
              </select>
            </div>
            {errors.state && (
              <p className="text-red-500 text-sm">{errors.state}</p>
            )}
          </div>

          {/* Vehicle Details - only show if we don't have VIN */}
          {!hasVIN && (
            <>
              {/* Year */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Year <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <select
                    value={formData.year}
                    onChange={(e) => handleInputChange("year", e.target.value)}
                    className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                      errors.year ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select year...</option>
                    {YEARS.map((year) => (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.year && (
                  <p className="text-red-500 text-sm">{errors.year}</p>
                )}
              </div>

              {/* Make */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Make <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.make}
                  onChange={(e) => handleInputChange("make", e.target.value)}
                  disabled={loadingStates.makes}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                    errors.make ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">
                    {loadingStates.makes
                      ? "Loading makes..."
                      : "Select make..."}
                  </option>
                  {vehicleOptions.makes.map((make) => (
                    <option key={make.id} value={make.name}>
                      {make.name}
                    </option>
                  ))}
                </select>

                {/* <select
                  value={formData.make}
                  onChange={(e) => handleInputChange("make", e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                    errors.make ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select make...</option>
                  {CAR_MAKES.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select> */}
                {errors.make && (
                  <p className="text-red-500 text-sm">{errors.make}</p>
                )}
              </div>

              {/* Model */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Model <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.model}
                  onChange={(e) => handleInputChange("model", e.target.value)}
                  disabled={!formData.make || loadingStates.models}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                    errors.model ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">
                    {loadingStates.models
                      ? "Loading models..."
                      : !formData.make
                      ? "Select make first"
                      : "Select model..."}
                  </option>
                  {vehicleOptions.models.map((model) => (
                    <option key={model.id} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                </select>
                {/* <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => handleInputChange("model", e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.model ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter vehicle model"
                /> */}
                {errors.model && (
                  <p className="text-red-500 text-sm">{errors.model}</p>
                )}
              </div>
            </>
          )}

          {/* Mileage - always required */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Current Mileage <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Gauge className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => handleInputChange("mileage", e.target.value)}
                className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.mileage ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter current mileage"
                min="0"
                max="500000"
              />
            </div>
            {errors.mileage && (
              <p className="text-red-500 text-sm">{errors.mileage}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Next Steps Info */}
      <Card className="bg-green-50 border-green-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-green-500 mt-1" />
            <div>
              <p className="font-medium text-green-900">
                Next: Get Your Extended Warranty Quote
              </p>
              <p className="text-sm text-green-700">
                Once you complete this information, you'll be connected you with
                the best extended warranty providers based on your profile.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {errors.general && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{errors.general}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium ${
            !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {onBack && (
          <button
            onClick={onBack}
            className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Vehicle Selection</span>
          </button>
        )}
      </div>

      {/* Debug info - remove in production */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Card className="bg-gray-50 border-gray-200">
          <CardHeader>
            <CardTitle className="text-sm">Debug Info</CardTitle>
          </CardHeader>
          <CardContent className="text-xs space-y-2">
            <p><strong>Has VIN:</strong> {hasVIN ? 'Yes' : 'No'}</p>
            <p><strong>Has License + State:</strong> {hasLicenseAndState ? 'Yes' : 'No'}</p>
            <p><strong>Required Fields:</strong> {Object.keys(getRequiredFields()).join(', ')}</p>
            <p><strong>Form Data:</strong></p>
            <pre className="bg-white p-2 rounded text-xs overflow-auto">
              {JSON.stringify(formData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )} */}
    </div>
  );
};

export default ExtendedWarrantyVehicleCompletion;
